import { useState, useEffect, useRef } from 'react';
import AdminService from "../services/adminService";
import { useDispatch } from "react-redux";
import { refreshDataUserService } from "../store/userSlice";
import LoadingForButton from "./LoadingForButton";
import { useTranslation } from 'react-i18next';
import { initializePaddle } from '@paddle/paddle-js';

export default function SubscriptionManagerOneTimePayment({ user, wedding }) {
    const [paddle, setPaddle] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);

    const userRef = useRef(user);

    const PAYMENT_AMOUNT = 50;
    const isPremium = user.subscription === 'premium';
    const isActive = user.subscriptionStatus === 'active';
    const hasPremiumLocation = user.hasPremiumLocation;
    const premiumLocationName = wedding?.location || 'Vaša odabrana lokacija';
    // const priceId = "pri_01kjtsw4zqy9s5fzg5zjevgvgj"; // 1 €
    const priceId = "pri_01kh2aggwnd8wyr7gf8hd1qpaj"; // 50 €
    const paddleClientToken = "live_9450ed5ad03263ad9a4ad90a8a5";

    const { t } = useTranslation();
    const dispatch = useDispatch();

    const expiryDate = new Date(user.premiumExpiryDate);
    const daysUntilExpiry = expiryDate
        ? Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24))
        : null;

    useEffect(() => {
        userRef.current = user;
    }, [user]);

    useEffect(() => {
        initializePaddle({
            token: paddleClientToken,
            // environment: 'sandbox', // 'production' za live
            eventCallback: (event) => {
                // console.log('Paddle event:', event);

                if (event.name === 'checkout.completed') {
                    setSuccess(t('paymentSuccess'));
                    setLoading(true);
                    let attempts = 0;

                    const pollForUpdate = () => {
                        attempts++;

                        setTimeout(() => {
                            dispatch(refreshDataUserService());

                            const currentUser = userRef.current;

                            if ((currentUser?.subscription === 'premium' && currentUser?.subscriptionStatus === 'active') || attempts >= 8) {
                                setLoading(false);
                            } else {
                                pollForUpdate();
                            }
                        }, 2000);
                    };

                    pollForUpdate();
                }

                if (event.name === 'checkout.closed') {
                    // User zatvorio checkout bez plaćanja
                    setLoading(false);
                }

                if (event.name === 'checkout.error') {
                    setError('Payment failed. Please try again.');
                    setLoading(false);
                }
            }
        }).then((paddleInstance) => {
            if (paddleInstance) {
                setPaddle(paddleInstance);
                // console.log('✅ Paddle initialized');
            }
        }).catch((error) => {
            console.error('❌ Paddle init failed:', error);
            setError('Payment system failed to load. Please refresh.');
        });
    }, []);

    const handlePayment = () => {
        if (!paddle) {
            setError('Payment system not ready. Please refresh.');
            return;
        }

        setLoading(true);
        setError('');

        paddle.Checkout.open({
            items: [{
                priceId: priceId,
                quantity: 1
            }],
            customData: {
                userId: user._id
            },
            customer: {
                email: user.email
            }
        });
    };

    const handlePaymentStripe = async () => {
        setLoading(true);
        setError('');
        setSuccess('');
        AdminService.createOneTimeCheckoutSession({ priceId })
            .then((res) => {
                window.location.href = res.data.url;
                setLoading(false);
            })
            .catch((err) => {
                setError(err.response.data.message);
                setLoading(false);
            })
    };

    return (
        <div className="">
            {/* Header */}
            <div className="text-center mb-2">
                {/* <h1 className="text-2xl font-extrabold text-gray-900">
                    {t('premiumAccess')}
                </h1> */}
                {/* <p className="mt-2 text-gray-600">
                    {hasPremiumLocation
                        ? t('premiumLocationPartner')
                        : t('oneTimePaymentInfo')}
                </p> */}
            </div>

            {/* Alerts */}
            {error && (
                <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-start">
                    <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span>{error}</span>
                </div>
            )}

            {success && (
                <div className="mb-6 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-start">
                    <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>{success}</span>
                </div>
            )}

            {/* Active Premium Status - WITH PREMIUM LOCATION */}
            {isPremium && isActive && hasPremiumLocation && (
                <div className="mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl shadow-lg p-6 border-2 border-blue-500">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></div>
                                <h2 className="text-2xl font-bold text-gray-900">
                                    {t('premiumPartnerActive')}
                                </h2>
                            </div>

                            {/* Ime lokacije - prominentno prikazano */}
                            <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-blue-600">
                                <p className="text-sm font-medium text-gray-900 mb-1">
                                    {t('premiumEnabledByLocation')}
                                </p>
                                <p className="text-xl font-bold text-gray-900 capitalize">
                                    {premiumLocationName}
                                </p>
                            </div>
                            <div className="space-y-2 mt-4">
                                <p className="text-gray-700">
                                    {t('weddingDate')}: {' '}
                                    <span className="font-semibold text-gray-900">
                                        {wedding.date ? new Date(wedding.date).toLocaleDateString('hr-HR', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                        }) : t('notSet')}
                                    </span>
                                </p>
                                <p className="text-gray-700">
                                    {t('premiumExpires')}: {' '}
                                    <span className="font-semibold text-gray-900">
                                        {expiryDate ? expiryDate.toLocaleDateString('hr-HR', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                        }) : t('notSet')}
                                    </span>
                                </p>
                                {daysUntilExpiry !== null && (
                                    <div className={`mt-3 px-4 py-2 rounded-lg ${daysUntilExpiry <= 3
                                        ? 'bg-red-100 border border-red-300'
                                        : 'bg-blue-100 border border-blue-300'
                                        }`}>
                                        <p className={`font-semibold ${daysUntilExpiry <= 3 ? 'text-red-800' : 'text-blue-800'
                                            }`}>
                                            {daysUntilExpiry > 0
                                                ? `${t('daysRemaining')} ${daysUntilExpiry}`
                                                : t('expired')}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Active Premium Status - WITHOUT PREMIUM LOCATION (paid) */}
            {isPremium && isActive && !hasPremiumLocation && (
                <div className="mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl shadow-lg p-6 border-2 border-blue-500">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></div>
                                <h2 className="text-2xl font-bold text-gray-900">
                                    {t('premiumActive')}
                                </h2>
                            </div>
                            <div className="space-y-2 mt-4">
                                <p className="text-gray-700">
                                    {t('weddingDate')}: {' '}
                                    <span className="font-semibold text-gray-900">
                                        {wedding.date ? new Date(wedding.date).toLocaleDateString('hr-HR', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                        }) : t('notSet')}
                                    </span>
                                </p>
                                <p className="text-gray-700">
                                    {t('premiumExpires')}: {' '}
                                    <span className="font-semibold text-gray-900">
                                        {expiryDate ? expiryDate.toLocaleDateString('hr-HR', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                        }) : t('notSet')}
                                    </span>
                                </p>
                                {daysUntilExpiry !== null && (
                                    <div className={`mt-3 px-4 py-2 rounded-lg ${daysUntilExpiry <= 3
                                        ? 'bg-red-100 border border-red-300'
                                        : 'bg-blue-100 border border-blue-300'
                                        }`}>
                                        <p className={`font-semibold ${daysUntilExpiry <= 3 ? 'text-red-800' : 'text-blue-800'
                                            }`}>
                                            {daysUntilExpiry > 0
                                                ? `${t('daysRemaining')} ${daysUntilExpiry}`
                                                : t('expired')}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Payment Card - Only show if NO premium OR if premium expired */}
            {(!isPremium || !isActive) && !hasPremiumLocation && (
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6 text-white">
                        <h2 className="text-2xl font-bold mb-2">{t('premiumPackage')}</h2>
                        <p className="text-blue-100">
                            {t('packageSubtitle')}
                        </p>
                    </div>

                    <div className="p-8">
                        {/* Price */}
                        <div className="text-center mb-8 pb-8 border-b border-gray-200">
                            {/* Trust badge — jasno da je jednokratno */}
                            <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-4 py-1.5 mb-4">
                                <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <span className="text-xs font-bold text-green-700 uppercase tracking-wide">
                                    {t('oneTimeNoSubscription')}
                                </span>
                            </div>

                            <div className="flex items-baseline justify-center mb-2">
                                <span className="text-5xl font-semibold text-gray-900">
                                    € {PAYMENT_AMOUNT}
                                </span>
                            </div>
                            <p className="text-gray-600 font-medium">
                                {t('oneTimePayment')}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                                {t('accessUntil7Days')}
                            </p>
                        </div>

                        {/* Features */}
                        <div className="mb-8">
                            <h3 className="font-bold text-gray-900 mb-4 text-lg">
                                {t('whatYouGet')}
                            </h3>
                            <ul className="space-y-3">
                                {[
                                    t('unlimitedGuests'),
                                    t('unlimitedTables'),
                                    t('rsvpTracking'),
                                    t('emailSupport'),
                                    t('weekAfterWedding'),
                                ].map((feature, index) => (
                                    <li key={index} className="flex items-start">
                                        <svg
                                            className="w-6 h-6 text-green-500 mr-3 flex-shrink-0"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        <span className="text-gray-700 text-lg">{feature}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* CTA Button */}
                        <button
                            onClick={handlePayment}
                            disabled={loading}
                            className="w-full flex items-center justify-center bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-4 px-6 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? <LoadingForButton /> : t('pay', { amount: PAYMENT_AMOUNT })}
                        </button>
                        <div className="mt-6 pt-5 border-t border-gray-100 text-center">
                            <p className="text-sm text-gray-500">
                                {t('alternativePaymentMethod')}{' '}
                                <a
                                    href="mailto:weddingherc@gmail.com"
                                    className="text-blue-600 hover:text-blue-800 font-semibold underline decoration-blue-200 hover:decoration-blue-500 transition-colors"
                                >
                                    weddingherc@gmail.com
                                </a>
                            </p>
                        </div>

                        <p className="text-center text-sm text-gray-500 mt-4">
                            {/* {t('secureStripePayment')} */}
                        </p>
                        {/* <label className="flex items-center mt-3 text-sm">
                            <input
                                type="checkbox"
                                checked={isCheckboxChecked}
                                onChange={(e) => setIsCheckboxChecked(e.target.checked)}
                                className="mr-2"
                                required
                            />
                            {t('securePaddlePayment')}
                        </label> */}
                        {/* {!isCheckboxChecked && <p className="text-red-500 text-xs mt-1">{t('checkboxRequiredMessage')}</p>} */}
                    </div>
                </div>
            )}

            {/* Premium Partner Info Card */}
            {hasPremiumLocation && (
                <div className="mt-8 bg-gradient-to-r from-blue-100 to-pink-200 rounded-2xl p-6 border border-blue-200">
                    <div className="flex items-center gap-3">
                        <svg className="w-5 h-5 text-purple-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <p className="text-gray-700">
                            <span className="font-semibold capitalize">{premiumLocationName}</span> {t('premiumProvidedByLocation')}
                        </p>
                    </div>
                </div>
            )}

            {/* Info Section */}
            <div className="mt-12 bg-gray-50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">
                    {t('faq')}
                </h3>
                <div className="space-y-4">
                    {hasPremiumLocation ? (
                        <>
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-2">
                                    {t('whatIsPremiumPartner')}
                                </h4>
                                <p className="text-gray-600">
                                    {t('whatIsPremiumPartnerAnswer')}
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-2">
                                    {t('doINeedToPay')}
                                </h4>
                                <p className="text-gray-600">
                                    {t('doINeedToPayAnswer')}
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-2">
                                    {t('howLongAccess')}
                                </h4>
                                <p className="text-gray-600">
                                    {t('howLongAccessAnswer')}
                                </p>
                            </div>
                        </>
                    ) : (
                        <>
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-2">
                                    {t('howLongAccess')}
                                </h4>
                                <p className="text-gray-600">
                                    {t('premiumDurationInfo')}
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-2">
                                    {t('whatHappensAfterExpiry')}
                                </h4>
                                <p className="text-gray-600">
                                    {t('afterExpiryInfo')}
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-2">
                                    {t('isThisMonthlySubscription')}
                                </h4>
                                <p className="text-gray-600">
                                    {t('isThisMonthlySubscriptionAnswer')}
                                </p>
                            </div>
                            {/* <div>
                                <h4 className="font-semibold text-gray-900 mb-2">
                                    {t('isPaymentSecure')}
                                </h4>
                                <p className="text-gray-600">
                                    {t('paymentSecurityInfo')}
                                </p>
                            </div> */}

                        </>
                    )}
                    {!isPremium && !isActive && (
                        <div>
                            <h4 className="font-semibold text-gray-900 mb-2">
                                {t('otherPaymentMethods')}
                            </h4>
                            <p className="text-gray-600">
                                {t('otherPaymentMethodsAnswer')}{' '}
                                <a href="mailto:weddingherc@gmail.com" className="text-blue-600 hover:underline font-medium">
                                    weddingherc@gmail.com
                                </a>.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
